(ns app.front.ui.text-editor
  (:require [app.front.ui :as ui]
            [app.front.ui.content-switcher :as ui-cs]
            [app.front.util :as futil]
            [cljs.reader]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [>evt]]
            [core.front.icons :as i]
            [core.util.schemas :as schemas]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema.core :as s]))

(rf/reg-sub ::edit-states
  :-> ::edit-states)

(rf/reg-sub ::editing?
  :<- [::edit-states]
  (fn [edit-states [_ k]]
    (contains? edit-states k)))

(rf/reg-event-db ::start-edit
  (fn [db [_ k]]
    (update db ::edit-states #(conj (or % #{}) k))))

(rf/reg-event-db ::end-edit
  (fn [db [_ k]]
    (update db ::edit-states disj k)))

(defn clear-external-edit-state
  [db]
  (dissoc db ::edit-states))

(defn with-external-edit-state [props k]
  (-> props
      (assoc :*editing? (rf/subscribe [::editing? k]))
      (assoc :on-start-edit #(>evt [::start-edit k]))
      (assoc :on-end-edit #(>evt [::end-edit k]))))

(defn header
  "An editor header component.
  Props:
  `:tr`             tr
  `:title`          string, the text shown on the header
  `:editable?`      boolean, true if the edit icon is visible
  `:editing?`       boolean, true if the user is in editing mode right now
  `:on-edit-toggle` fn, called when the edit icon is clicked by the user"
  [{:keys [tr title replace-template editable? editing? on-edit-toggle changes?]}]
  [:div.w-full.flex
   [:div.flex-1.items-center title]
   replace-template
   (when (and editable?
              (or (not changes?) (not editing?)))
     [:div
      (cc/button {:icon             (if editing? i/remove i/pen)
                  :kind             :ghost
                  :class-name       "clickable cursor-pointer"
                  :icon-description ""
                  :data-cy          "edit-toggle"
                  :on-click         on-edit-toggle}
                 (tr [(if editing? :close :edit)]))])])


(defn- editor-text-areas
  "Each textarea is half the width of the page.
  If there is an odd number of textareas an empty one is appended to the list to fill the page."
  [tr editing? small? contents *values *selected class]
  [:div.w-full.flex.flex-col.mt-2.w-2-3
   {:class (if small? "min-h-20" "min-h-128")}
   (when (-> contents count (> 1))
     [ui-cs/content-switcher {:items     (-> contents count range)
                              :val->str  (comp tr vector :label (partial nth contents))
                              :current   @*selected
                              :on-change #(reset! *selected %)
                              :class     "mb-4"}])
   (let [{:keys [value index]} (nth contents @*selected)]
     [ui/text-area {:disabled    (not editing?)
                    :data-cy     "text-content"
                    :class       ["flex-1" class]
                    :input-class "h-full"
                    :light       true
                    :value       (if editing?
                                   (get @*values index)
                                   value)
                    :on-change   #(swap! *values assoc index (futil/tval %))}])])


(defn- editor-buttons
  "The save and cancel buttons as well as the optional extra button"
  [tr editing? on-end-edit contents *values on-save extra-button has-changes?]
  [:div.flex.justify-between.my-2
   [:div extra-button]
   (when (and editing? has-changes?)
     [:div
      (cc/button {:kind     :tertiary
                  :on-click (fn [_]
                              (on-end-edit)
                              (reset! *values (mapv :value contents)))}
                 (tr [:cancel]))
      (cc/button {:kind     :primary
                  :on-click (fn [_]
                              (apply on-save (map #(or % "") @*values)))}
                 (tr [:save]))])])

(defn editor-external-state
  "Like `editor`, but keep state externally.

  Needs `*values` and `*selected` additionally."
  [{:keys [tr
           class
           contents
           on-save
           instructions
           extra-button
           read-only?
           *editing?
           small?
           on-start-edit
           on-end-edit
           always-show?
           *values
           *selected]
    :as   props}
   children]
  (let [on-toggle-edit (if (and on-end-edit on-start-edit)
                         #(if @*editing?
                            (on-end-edit)
                            (on-start-edit))
                         #(swap! *editing? not))
        on-end-edit    (or on-end-edit #(reset! *editing? false))
        contents       (->> contents
                            (map-indexed (fn [index v] (assoc v :index index))))
        changes?       (not= @*values (mapv :value contents))]
    [:div.verdict-template-page {:class (when @*editing? "editing")}
     [header (assoc props
                    :editable? (not read-only?)
                    :editing? @*editing?
                    :changes? changes?
                    :on-edit-toggle (fn [_]
                                      (reset! *values (mapv :value contents))
                                      (on-toggle-edit)))]
     (when (or @*editing? always-show?)
       [:div.flex.w-full
        instructions
        [editor-text-areas tr @*editing? small? contents *values *selected class]])
     [editor-buttons tr @*editing? on-end-edit contents *values on-save extra-button changes?]
     children]))

(defn editor
  "A UI component for generic editable text areas, mainly for (authority) admin views.
  See `app.front.ui.template-editor/editor` for an editor for HTML/CSS that uses this as a base.

  Has by default a textarea for each editable content as well as save and reset buttons.

  Props:
  `:tr`            tr
  `:title`         string, the text to be shown in the header (passed on to `header`)
  `:contents`      seq of maps, with the following structure:
                   {:value (string, what the editable text should be by default)
                    :label (keyword, the translation key for the label above the textarea)}
  `:on-save`       fn, takes as parameters the saved strings, using the same order as in `:contents`
  `:instructions`  reagent fragment, added after the header but before the textareas
  `:extra-button`  reagent fragment, added before the save and reset buttons
  `:*editing?`     reagent atom, true if the editor is in editing mode (by default provided internally)
  `:small?`        if true, render as 20 rows tall, otherwise 50
  `:on-start-edit` fn, called when the editor enters editing mode (by default resets *editing?)
  `:on-end-edit`   fn, called when the editor exits editing mode (by default resets *editing?)"
  [{:keys [contents *editing? on-end-edit]
    :as   props}
   children]
  (r/with-let [*values   (r/atom (mapv :value contents)) ; mapv because vectors support assoc for updating items
               *editing? (or *editing? (r/atom false))
               *selected (r/atom 0)]
    [editor-external-state
     (merge props
            (util/mmap *values *selected *editing? on-end-edit))
     children]))

(defn- valid-email-recipients? [value]
  (or (-> value str-util/blank->nil nil?)
      (let [emails (cond-> value (string? value) (str/split #","))]
        (when (coll? emails)
          (->> emails
               (every? #(-> %
                            str/trim
                            (->> (s/check schemas/Email))
                            nil?)))))))

(defn emails-editor
  "Editor for comma separated email list. Provides set of valid emails.

  Required props:
  `:recipients` initial value, accepts collection or comma separated string
  `:value-fn`   state subscription
  `:set-fn`     on-blur, shall store raw string value to state. Can contain invalid entries
  `:save-fn`    on-blur, receives set of valid emails."
  [{:keys [recipients value-fn set-fn save-fn id label-text invalid-text]}]
  [ui/text-area (util/mmap {:invalid       (not (valid-email-recipients? (value-fn)))
                            :default-value (or (value-fn)
                                               (if (string? recipients)
                                                 recipients
                                                 (->> recipients (str/join ","))))
                            :on-blur       #(let [target (futil/tval %)]
                                              (set-fn target)
                                              (when (valid-email-recipients? target)
                                                (save-fn (some-> (str-util/blank->nil target)
                                                                 (str/split #",")
                                                                 (->> (map str/trim))
                                                                 set))))}
                           id label-text invalid-text)])
